import type { Person } from '@amarantos/api-client'
import { getListPersonsQueryOptions, useListPersonsSuspense } from '@amarantos/api-client'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useDeferredValue } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog'
import { DeleteButton } from '@/components/delete-button'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { TableActions } from '@/components/ui/table'
import { AccessDrawer } from '@/features/users/components/access-drawer'
import { UserFormDrawer, type UserFormDrawerState } from '@/features/users/components/user-form-drawer'
import { UsersFilters } from '@/features/users/components/users-filters'
import { UsersTable } from '@/features/users/components/users-table'
import { useDeletePersonMutation } from '@/features/users/hooks/use-person-mutations'
import { toListPersonsParams, type UsersSearch, usersSearchSchema } from '@/features/users/utils/search'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { getApiErrorMessage } from '@/utils/errors'
import { requirePanelRole, usersRequiredRoles } from '@/utils/roles'

function toFormDrawerState(search: UsersSearch): UserFormDrawerState {
	switch (search.mode) {
		case 'create':
			return { mode: 'create' }
		case 'edit':
			return { mode: 'edit', personId: search.personId }
		default:
			return { mode: undefined }
	}
}

export const Route = createFileRoute('/_authenticated/users')({
	validateSearch: usersSearchSchema,
	beforeLoad: ({ context: { queryClient } }) => requirePanelRole(queryClient, usersRequiredRoles),
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(getListPersonsQueryOptions(toListPersonsParams(deps)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_users' />
	),
	component: UsersRoute,
})

function UsersRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = useNavigate({ from: '/users' })
	const deferredSearch = useDeferredValue(search)
	const isStale = deferredSearch !== search

	const usersQuery = useListPersonsSuspense(toListPersonsParams(deferredSearch))

	const deletePerson = useConfirmAction<Person, { id: number }>({
		mutation: useDeletePersonMutation(),
		toVariables: (person) => ({ id: person.id }),
		formatError: (err) => getApiErrorMessage(err, t('error.delete_user')),
	})

	const users = usersQuery.data?.data ?? []
	const meta = usersQuery.data?.meta
	const currentPage = search.page
	const totalPages = meta?.total_pages ?? 1

	const formDrawerState = toFormDrawerState(search)
	const formDrawerOpen = formDrawerState.mode !== undefined
	const accessDrawerOpen = search.mode === 'access'
	const accessPersonId = search.mode === 'access' ? search.personId : undefined

	const closeDrawer = (isOpen: boolean) => {
		if (!isOpen) {
			navigate({
				search: (previous) => ({ ...previous, mode: undefined, personId: undefined }),
			})
		}
	}

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.users') }]}>
				<PageTitle
					subtitle={t('users.page.description')}
					actions={
						<Button asChild>
							<Link to='.' search={(previous) => ({ ...previous, mode: 'create' as const, personId: undefined })}>
								{t('users.create.button')}
							</Link>
						</Button>
					}
				>
					{t('nav.users')}
				</PageTitle>
			</PageHeader>
			<PageContent>
				<Card>
					<CardContent className='space-y-4'>
						<UsersFilters
							onApply={() => {
								navigate({
									search: (previous) => ({ ...previous, page: 1 }),
								})
							}}
						/>

						<UsersTable
							users={users}
							isStale={isStale}
							actions={(user) => (
								<TableActions>
									<Button asChild variant='outline' size='sm'>
										<Link
											to='.'
											search={(previous) => ({
												...previous,
												mode: 'edit' as const,
												personId: user.id,
											})}
										>
											{t('users.edit.button')}
										</Link>
									</Button>
									<Button asChild variant='outline' size='sm'>
										<Link
											to='.'
											search={(previous) => ({
												...previous,
												mode: 'access' as const,
												personId: user.id,
											})}
										>
											{t('users.access.button')}
										</Link>
									</Button>
									<DeleteButton onClick={() => deletePerson.request(user)} />
								</TableActions>
							)}
						/>

						<PaginationBar
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
						/>
					</CardContent>
				</Card>

				<UserFormDrawer {...formDrawerState} open={formDrawerOpen} onOpenChange={closeDrawer} />

				<AccessDrawer personId={accessPersonId} open={accessDrawerOpen} onOpenChange={closeDrawer} />

				<ConfirmDeleteDialog
					{...deletePerson.dialogProps}
					title={t('users.delete.title')}
					description={t('users.delete.description', {
						name: deletePerson.target?.name ?? t('users.delete.fallback_name'),
					})}
				/>
			</PageContent>
		</>
	)
}
